/**
 * src/pages/support/MyTickets.js
 *
 * User's support ticket list with status filters and search.
 */

import React, { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Search, X } from "lucide-react";
import { useTickets } from "../../hooks/useSupport";
import TicketCard from "../../components/support/TicketCard";
import NoData from "../../components/NoData";
import { errorDev } from '../../utils/logger.js';

export default function MyTickets() {
  const { t, ready: i18nReady } = useTranslation("support");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const safeT = useCallback(
    (key, fallback) => {
      if (!i18nReady) return fallback;
      try {
        const result = t(key);
        return result === key ? fallback : result;
      } catch (err) {
        errorDev("Operation failed:", err);
        return fallback;
      }
    },
    [t, i18nReady]
  );

  const [tickets, loading, error] = useTickets({ status: statusFilter || undefined });

  // Client-side search filter
  const filtered = useMemo(() => {
    if (!searchInput.trim()) return tickets;
    const q = searchInput.trim().toLowerCase();
    return tickets.filter(
      (t) =>
        (t.subject || "").toLowerCase().includes(q) ||
        (t.lastMessageSnippet || "").toLowerCase().includes(q)
    );
  }, [tickets, searchInput]);

  const statuses = [
    { value: "", label: safeT("filter_all", "All statuses") },
    { value: "open", label: safeT("filter_open", "Open") },
    { value: "pending", label: safeT("filter_pending", "Pending") },
    { value: "in_progress", label: safeT("ticket_status_in_progress", "In Progress") },
    { value: "resolved", label: safeT("filter_resolved", "Resolved") },
    { value: "closed", label: safeT("filter_closed", "Closed") },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                {safeT("tickets_title", "My Support Tickets")}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {safeT("tickets_subtitle", "Track your requests and responses.")}
              </p>
            </div>
            <Link
              to="/support"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{safeT("back_to_help", "Help Center")}</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={safeT("search_placeholder", "Search tickets...")}
              className="ui-input pl-10 pr-8"
              aria-label="Search tickets"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="ui-input w-auto"
            aria-label="Filter by status"
          >
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Ticket List */}
        <section aria-live="polite">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="ui-card rounded-xl p-4">
                  <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-neutral-700 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="rounded-xl p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm" role="alert">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <NoData
              title={safeT("tickets_empty", "No tickets yet")}
              description={safeT("tickets_empty_desc", "Open a ticket if you need help.")}
            />
          ) : (
            <div className="space-y-3">
              {filtered.map((ticket) => (
                <TicketCard key={ticket.id} ticket={ticket} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
